// src/utils/api.js

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5134";

// Function to handle HTTP errors
const handleResponse = async (response) => {
	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "Something went wrong");
	}
	return response.json();
};

// API Functions
export const loginUser = async (username, password) => {
	const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ username, password }),
	});

	const data = await handleResponse(response);
	if (data.token) {
		localStorage.setItem("token", data.token);
		localStorage.setItem("userData", JSON.stringify(data.data));
	}
	return data;
};

export const registerUser = async (role, username, instructorIds, firstName, lastName, email, phoneNumber, homeAddress) => {
	const token = getToken();
	if (!token) throw new Error("No authentication token found");

	try {
		const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`
			},
			body: JSON.stringify({
				role,
				username,
				instructorIds,
				firstName,
				lastName,
				email,
				phoneNumber,
				homeAddress,
			}),
		});
		return handleResponse(response);
	} catch (error) {
		console.error("Register user error:", error);
		throw error;
	}
};

export const registerChildAccount = async (parentId, username, firstName, lastName, birthDate) => {
	const token = getToken();
	if (!token) throw new Error("No authentication token found");

	try {
		const response = await fetch(`${API_BASE_URL}/api/auth/registerChildAccount`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`
			},
			body: JSON.stringify({
				parentId,
				username,
				firstName,
				lastName,
				birthDate,
			}),
		});
		return handleResponse(response);
	} catch (error) {
		console.error("Register child account error:", error);
		throw error;
	}
};

export const searchUsers = async ({ name = '', role = '', locationId = '', userId = '', limit = 20, skip = 0 } = {}) => {
	const token = getToken();
	if (!token) throw new Error("No authentication token found");

	// Validate parameters
	const validatedParams = {
		name: String(name),
		role: String(role),
		locationId: String(locationId),
		userId: String(userId),
		limit: Math.max(1, parseInt(limit) || 20),  // Ensure positive number, default to 10
		skip: Math.max(0, parseInt(skip) || 0)      // Ensure non-negative, default to 0
	};

	const queryParams = new URLSearchParams(validatedParams).toString();

	try {
		const response = await fetch(`${API_BASE_URL}/api/users/search?${queryParams}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`
			},
		});
		return handleResponse(response);
	} catch (error) {
		// Handle network errors
		throw new Error(`Failed to search users: ${error.message}`);
	}
};

export const searchUser = async ({ userId = '' } = {}) => {
	const token = getToken();
	if (!token) throw new Error("No authentication token found");

	// Validate parameters
	const validatedParams = {
		userId: String(userId),
	};

	const queryParams = new URLSearchParams(validatedParams).toString();

	try {
		const response = await fetch(`${API_BASE_URL}/api/user/search?${queryParams}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`
			},
		});
		return handleResponse(response);
	} catch (error) {
		// Handle network errors
		throw new Error(`Failed to search users: ${error.message}`);
	}
};

// export const getUsers = async () => {
// 	const token = getToken();
// 	if (!token) throw new Error("No authentication token found");

// 	try {
// 		const response = await fetch(`${API_BASE_URL}/api/users`, {
// 			method: "GET",
// 			headers: {
// 				"Content-Type": "application/json",
// 				"Authorization": `Bearer ${token}`
// 			},
// 		});
// 		return handleResponse(response);
// 	} catch (error) {
// 		console.error("Get users error:", error);
// 		throw error;
// 	}
// };

export const getSubjects = async () => {
	const response = await fetch(`${API_BASE_URL}/api/subjects`);
	return handleResponse(response);
};

export const getCourses = async (subjectId) => {
	const response = await fetch(`${API_BASE_URL}/api/subjects/${subjectId}`);
	return handleResponse(response);
};

export const getToken = () => localStorage.getItem("token") || null;

export const logout = async () => {
	const token = getToken();
	if (token) {
		try {
			await fetch(`${API_BASE_URL}/api/auth/logout`, {
				method: "POST",
				headers: {
					"Authorization": `Bearer ${token}`
				},
			});
		} finally {
			localStorage.removeItem("token");
			localStorage.removeItem("userData");
		}
	}
};

export const updateUserProfile = async (userData) => {
	const token = getToken();
	if (!token) throw new Error("No authentication token found");

	try {
		const response = await fetch(`${API_BASE_URL}/api/users/updateProfile`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`
			},
			body: JSON.stringify(userData),
		});
		const data = await handleResponse(response);

		// Update local storage with new user data if it's the current user
		const currentUserData = JSON.parse(localStorage.getItem("userData") || "{}");
		if (currentUserData._id === data.user._id) {
			localStorage.setItem("userData", JSON.stringify(data.user));
		}

		return data;
	} catch (error) {
		console.error("Update profile error:", error);
		throw error;
	}
};

export const updateUserProgress = async (userData) => {
	const token = getToken();
	if (!token) throw new Error("No authentication token found");

	try {
		const response = await fetch(`${API_BASE_URL}/api/users/updateProgress`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`
			},
			body: JSON.stringify(userData),
		});

		const data = await handleResponse(response);

		// Update local storage with new user data if it's the current user
		const currentUserData = JSON.parse(localStorage.getItem("userData") || "{}");
		if (currentUserData._id === data.user._id) {
			localStorage.setItem("userData", JSON.stringify(data.user));
		}

		return data;
	} catch (error) {
		console.error("Update progress error:", error);
		throw error;
	}
};

export const registerLocation = async (locationData) => {
	// labName, address, city, state, zipCode, manageName, email, phone
	const token = getToken();
	if (!token) throw new Error("No authentication token found");

	try {
		const response = await fetch(`${API_BASE_URL}/api/auth/registerLocation`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`
			},
			body: JSON.stringify(locationData),
		});
		return handleResponse(response);
	} catch (error) {
		console.error("Location registration error:", error);
		throw error;
	}
};

export const updateLocation = async (locationData) => {
	const token = getToken();
	if (!token) throw new Error("No authentication token found");

	try {
		const response = await fetch(`${API_BASE_URL}/api/auth/updateLocation`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`
			},
			body: JSON.stringify(locationData),
		});
		const data = await handleResponse(response);

		return data;
	} catch (error) {
		console.error("Update location error:", error);
		throw error;
	}
};

export const searchLocations = async ({ text = '', locationId = '', limit = 20, skip = 0 } = {}) => {
	const token = getToken();
	if (!token) throw new Error("No authentication token found");

	// Validate parameters
	const validatedParams = {
		text: String(text),
		locationId: String(locationId),
		limit: Math.max(1, parseInt(limit) || 20),  // Ensure positive number, default to 10
		skip: Math.max(0, parseInt(skip) || 0)      // Ensure non-negative, default to 0
	};

	const queryParams = new URLSearchParams(validatedParams).toString();

	try {
		const response = await fetch(`${API_BASE_URL}/api/locations/search?${queryParams}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${token}`
			},
		});
		return handleResponse(response);
	} catch (error) {
		// Handle network errors
		throw new Error(`Failed to search users: ${error.message}`);
	}
};